import { Input } from 'antd';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { CustomInputNumberProps } from '@app/components/CustomInputNumber/CustomInputNumber.types';

import type { InputRef } from 'antd';
const DECIMAL_SEPARATOR = '.';
const DECIMAL_SCALE = 2;
const INTEGER_PART_MAX_AMOUNT = 11;

const isDigit = (char: string) => /^[0-9]$/.test(char);

const formatGrouped = (raw: string): string => {
  if (!raw) return '';

  const hasTrailingDot = raw.endsWith(DECIMAL_SEPARATOR);
  const normalized = raw.replace(/\s/g, '');

  const [integerPartRaw = '', fractionPartRaw] =
    normalized.split(DECIMAL_SEPARATOR);

  const integerPart = integerPartRaw.replace(/^0+(?=\d)/, '') || '0';
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  if (hasTrailingDot) return `${groupedInteger}${DECIMAL_SEPARATOR}`;

  if (fractionPartRaw !== undefined) {
    return `${groupedInteger}${DECIMAL_SEPARATOR}${fractionPartRaw}`;
  }

  return groupedInteger;
};

const sanitizeRaw = (
  input: string,
  allowDecimal: boolean,
  allowEmpty: boolean
): string => {
  const noSpaces = (input ?? '').replace(/\s/g, '');

  if (noSpaces === '') return allowEmpty ? '' : '0';
  const normalizedSeparator = noSpaces.replace(/,/g, DECIMAL_SEPARATOR);

  let cleaned = normalizedSeparator.replace(/[^\d.]/g, '');

  if (!allowDecimal) {
    cleaned = cleaned.replace(/\./g, '');
    cleaned = cleaned.slice(0, INTEGER_PART_MAX_AMOUNT);
    cleaned = cleaned.replace(/^0+(?=\d)/, '') || (allowEmpty ? '' : '0');
    return cleaned;
  }

  const firstDotIndex = cleaned.indexOf(DECIMAL_SEPARATOR);
  if (firstDotIndex !== -1) {
    const before = cleaned.slice(0, firstDotIndex);
    const after = cleaned.slice(firstDotIndex + 1).replace(/\./g, '');
    cleaned = `${before}${DECIMAL_SEPARATOR}${after}`;
  }

  let [whole = '', fraction] = cleaned.split(DECIMAL_SEPARATOR);

  if (whole === '' && firstDotIndex === 0) whole = '0';

  whole = whole.slice(0, INTEGER_PART_MAX_AMOUNT);
  whole = whole.replace(/^0+(?=\d)/, '') || '0';

  if (fraction !== undefined) {
    fraction = fraction.slice(0, DECIMAL_SCALE);
    return `${whole}${DECIMAL_SEPARATOR}${fraction}`;
  }

  if (firstDotIndex !== -1 && cleaned.endsWith(DECIMAL_SEPARATOR)) {
    return `${whole}${DECIMAL_SEPARATOR}`;
  }

  return allowEmpty ? (whole === '0' ? '0' : whole) : whole;
};

const isControlKey = (key: string) =>
  key === 'Backspace' ||
  key === 'Delete' ||
  key === 'Tab' ||
  key === 'Enter' ||
  key === 'Escape' ||
  key === 'ArrowLeft' ||
  key === 'ArrowRight' ||
  key === 'ArrowUp' ||
  key === 'ArrowDown' ||
  key === 'Home' ||
  key === 'End';

const isMetaCombo = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (!(event.ctrlKey || event.metaKey)) return false;
  const k = event.key.toLowerCase();
  return (
    k === 'a' || k === 'c' || k === 'v' || k === 'x' || k === 'z' || k === 'y'
  );
};

export const CustomInputNumber = ({
  onSuccess,
  className = '',
  style = {},
  addonBefore,
  addonAfter,
  isAllowed,
  dataTestId = '',
  name,
  inputValue = '',
  disabled = false,
  allowEmpty = true,
  size,
  allowDecimal = true,
  onBlur,
  placeholder = '',
}: CustomInputNumberProps) => {
  const inputRef = useRef<InputRef | null>(null);
  const selectionLogicalPosRef = useRef<number | null>(null);

  const sanitize = useCallback(
    (val: string) => sanitizeRaw(val, allowDecimal, allowEmpty),
    [allowDecimal, allowEmpty]
  );

  const [rawValue, setRawValue] = useState<string>(() =>
    sanitize(String(inputValue ?? ''))
  );

  useEffect(() => {
    setRawValue(sanitize(String(inputValue ?? '')));
  }, [inputValue, sanitize]);

  const displayValue = useMemo(() => formatGrouped(rawValue), [rawValue]);

  const setCaretByLogicalPos = useCallback((logicalPos: number) => {
    const native = inputRef.current?.input;
    if (!native) return;

    const formatted = native.value ?? '';

    let count = 0;
    let caret = 0;

    while (caret < formatted.length) {
      const ch = formatted[caret];
      if (isDigit(ch) || ch === DECIMAL_SEPARATOR) {
        count += 1;
        if (count >= logicalPos) {
          caret += 1;
          break;
        }
      }
      caret += 1;
    }

    native.setSelectionRange(caret, caret);
  }, []);

  useLayoutEffect(() => {
    const logical = selectionLogicalPosRef.current;
    if (logical === null) return;

    const native = inputRef.current?.input;
    if (!native) return;

    if (document.activeElement === native) {
      requestAnimationFrame(() => {
        setCaretByLogicalPos(logical);
        selectionLogicalPosRef.current = null;
      });
    } else {
      selectionLogicalPosRef.current = null;
    }
  }, [displayValue, setCaretByLogicalPos]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (isControlKey(event.key) || isMetaCombo(event)) return;

      if (event.key.length !== 1) return;

      if (isDigit(event.key)) return;

      if (
        allowDecimal &&
        (event.key === DECIMAL_SEPARATOR || event.key === ',')
      ) {
        return;
      }

      event.preventDefault();
    },
    [disabled, allowDecimal]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      event.preventDefault();
      const pasted = event.clipboardData.getData('text') ?? '';
      const next = sanitize(pasted);

      const floatValue =
        next === '' ? NaN : Number(next.replace(/\s/g, '').replace(',', '.'));
      if (next !== '' && Number.isNaN(floatValue)) return;

      if (next !== '' && isAllowed && !isAllowed(floatValue)) return;

      setRawValue(next);
      onSuccess?.({
        value: next,
        event: {
          target: { value: next } as unknown as EventTarget & HTMLInputElement,
        } as React.ChangeEvent<HTMLInputElement>,
      });
    },
    [disabled, sanitize, isAllowed, onSuccess]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const currentDisplay = event.target.value ?? '';
      const selectionStart =
        event.target.selectionStart ?? currentDisplay.length;

      const logicalPos = currentDisplay
        .slice(0, selectionStart)
        .replace(/[^\d.,]/g, '')
        .replace(/,/g, DECIMAL_SEPARATOR).length;

      selectionLogicalPosRef.current = logicalPos;

      const next = sanitize(currentDisplay);

      if (next === '') {
        setRawValue('');
        onSuccess?.({ value: '', event });
        return;
      }

      const floatValue = Number(next.replace(/\s/g, '').replace(',', '.'));
      if (Number.isNaN(floatValue)) {
        setRawValue(next);
        onSuccess?.({ value: next, event });
        return;
      }

      const isIntermediateZeroDecimal =
        allowDecimal && (next === '0' || next === '0.' || next === '0.0');

      if (isAllowed && !isAllowed(floatValue) && !isIntermediateZeroDecimal)
        return;

      setRawValue(next);
      onSuccess?.({ value: next, event });
    },
    [sanitize, onSuccess, allowDecimal, isAllowed]
  );

  return (
    <Input
      ref={inputRef}
      disabled={disabled}
      name={name}
      data-testid={dataTestId}
      className={className}
      style={style}
      value={displayValue}
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={handleChange}
      addonBefore={addonBefore}
      addonAfter={addonAfter}
      size={size}
      autoComplete="off"
      onBlur={onBlur}
      placeholder={placeholder}
    />
  );
};
